import React, { useEffect, useState, useContext } from 'react';
import { Table, Modal, message, Drawer, Input, Select, Row, Col, Button, Typography, Space, Pagination } from 'antd';
import { LoginContext } from '../../../../context/loginContext';
import { GlobalContext } from '../../../../context/globalContext';
import CDetalleVistaArticulo from './detalle-articulo-sucursal.js';
import { useNavigate } from 'react-router-dom';

const { Search } = Input;
const { Option } = Select;
const { Title } = Typography;

const CListaArticulosHabilitadosSucursal = () => {
  const { user } = useContext(LoginContext);
  const { categorias } = useContext(GlobalContext);
  const [articulos, setArticulos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [nombreFiltro, setNombreFiltro] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, total: 0, pageSize: 10 });

  const navigate = useNavigate();

  const fetchArticulos = async (page = 1) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      queryParams.append('limit', pagination.pageSize);
      if (nombreFiltro) queryParams.append('aem_nombre', nombreFiltro);
      if (categoriaFiltro) queryParams.append('aem_categoria_id', categoriaFiltro);
      queryParams.append('asu_habilitado', 'true');
      queryParams.append('asu_sucursal', `${user.usu_sucursal}`);

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/articulo-sucursal/sucursal/?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const json = await res.json();
      // El backend ya filtra por asu_habilitado, no necesitamos filtrar nuevamente
      setArticulos(json.data || []);
      setPagination({
        current: json.pagination?.currentPage ?? page,
        total: json.pagination?.total ?? 0,
        pageSize: pagination.pageSize
      });
    } catch (err) {
      console.error(err);
      message.error('Error al cargar artículos habilitados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticulos(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nombreFiltro, categoriaFiltro]);

  const handleTableChange = (page) => {
    fetchArticulos(page);
  };

  const renderCategorias = (nivel = 0, parentId = null) => {
    return categorias
      .filter(cat => cat.cat_padre_id === parentId)
      .flatMap(cat => [
        <Option key={cat.cat_id} value={cat.cat_id}>
          {'-'.repeat(nivel) + ' ' + cat.cat_nombre}
        </Option>,
        ...renderCategorias(nivel + 1, cat.cat_id)
      ]);
  };

  // === Botón de sincronización (dispara backend y refresca) ===
  const handleSync = async () => {
    setLoading(true);
    try {
      // deposito = sucursal del usuario
      const url = `${process.env.REACT_APP_API_URL}/sync/articulosucursal?deposito=${user.usu_sucursal}&verbose=true`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      const json = await res.json();
      if (!res.ok || !json.ok) {
        throw new Error(json?.error || 'Error al sincronizar');
      }

      message.success(`Sincronizado: ${json.updated} actualizados, ${json.skipped} sin cambios, ${json.notFound} no encontrados.`);
      await fetchArticulos();
    } catch (err) {
      console.error(err);
      message.error(`Falló la sincronización: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Nombre',
      dataIndex: ['articulo_empresa', 'aem_nombre'],
      key: 'nombre',
    },
    {
      title: 'Marca',
      dataIndex: ['articulo_empresa', 'aem_marca'],
      key: 'marca',
    },
    {
      title: 'Stock',
      dataIndex: ['datos_sucursal', 'asu_stock'],
      key: 'stock',
    },
    {
      title: 'Precio Final',
      dataIndex: ['datos_sucursal', 'asu_precio_con_descuento'],
      key: 'precio',
      render: (value) => (value != null ? `$${value}` : '-')
    }
  ];

  return (
    <div style={{ padding: '1rem' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: '1rem' }}>
        <Col><Title level={3}>Artículos habilitados</Title></Col>
        <Col>
          <Space>
            <Button onClick={() => setDrawerVisible(true)}>Filtros</Button>
            <Button 
              type="primary" 
              loading={loading} 
              onClick={handleSync}
              style={{ backgroundColor: '#01634C', borderColor: '#01634C', color: '#fff' }}
            >
              Sincronizar cambios
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={articulos}
        rowKey={(r) => r.articulo_empresa.aem_id}
        loading={loading}
        onRow={(record) => ({
          onClick: () => navigate(`/detalle-articulo-sucursal/${record.articulo_empresa.aem_id}`, {
            state: { articulo: record }
          })
        })}
        pagination={false}
      />

      <Pagination
        current={pagination.current}
        pageSize={pagination.pageSize}
        total={pagination.total}
        onChange={handleTableChange}
        style={{ marginTop: '1rem', textAlign: 'right' }}
        showSizeChanger={false}
      />

      <Drawer
        title="Filtros"
        placement="right"
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
      >
        <Search
          placeholder="Buscar por nombre"
          onSearch={(value) => setNombreFiltro(value)}
          enterButton
          allowClear
          style={{ marginBottom: '1rem' }}
        />

        <Select
          placeholder="Filtrar por categoría"
          allowClear
          onChange={(value) => setCategoriaFiltro(value)}
          style={{ width: '100%' }}
          value={categoriaFiltro}
        >
          {renderCategorias()}
        </Select>

        <Button
          style={{ marginTop: '1rem' }}
          type="default"
          onClick={() => {
            setNombreFiltro('');
            setCategoriaFiltro(null);
            setDrawerVisible(false);
          }}
        >
          Limpiar filtros
        </Button>
      </Drawer>
    </div>
  );
};

export default CListaArticulosHabilitadosSucursal;
